/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { observer } from 'mobx-react';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { history } from 'utils/history';
import { ExtractionTabContainer } from 'App/Pages/Project/IdpApplication/idp-project/idp-extraction-project/extraction-tab-container';
import { DocumentUpload } from 'App/Pages/Project/IdpApplication/idp-project/idp-extraction-project/document-upload';
import { idpApplicationStore, structuredExtractionStore } from 'stores';
import { useClusterStatus, useClusterConnectorSecrets } from 'App/Pages/Project/IdpApplication/hooks';
import { CLUSTER_STATUS } from 'utils/cluster/constants';
import { getIdFromSlug } from 'utils/getIdFromSlug';

import { ExtractedResults } from './extracted-results/ExtractedResults';
import * as Styled from './StructuredExtraction.styled';
import { TestTemplate } from './test-template/TestTemplate';
import { PublishTemplate } from './publish-template/PublishTemplate';

const DEFAULT_TAB = 'upload';
const VALID_TABS = ['upload', 'extract', 'evaluate'];

/** @typedef {import('./extracted-results/types').Document} Document */
/** @typedef {import('App/Pages/Project/IdpApplication/idp-project/utils/types').ExtractionResult} ExtractionResult */

/**
 * @typedef {Object} StructuredExtractionProps
 * @property {boolean} canUserModifyProject
 * @property {() => void} handleNavigationToVersions
 * @property {React.ReactElement} renderTitleContainer
 */

/**
 * @param {StructuredExtractionProps} props
 * @returns {JSX.Element}
 */
export const StructuredExtraction = observer(function StructuredExtraction({
  canUserModifyProject,
  renderTitleContainer,
  handleNavigationToVersions
}) {
  /**
   * @param {string|undefined} tab
   * @returns {string}
   */
  const getValidTab = (tab) => (VALID_TABS.includes(tab) ? tab : DEFAULT_TAB);
  const params = useParams();
  const slug = params['slug'] || '';
  const tabParam = typeof params['tab'] === 'string' ? params['tab'] : undefined;
  const initialTab = getValidTab(tabParam);
  const [selectedTab, setSelectedTab] = useState(initialTab);
  const [isPdfViewerVisible, setIsPdfViewerVisible] = useState(true);
  const {
    documentForExtraction,
    structuredExtractionResults,
    isExtractingDocumentTemplate,
    isUploading,
    testTemplateDocuments,
    loadStructuredResultsForTestTemplateDocuments
  } = structuredExtractionStore;

  const clusterId = idpApplicationStore.idpApplication?.clusterId;
  const clusterStatus = useClusterStatus(clusterId);
  const areIdpConnectorSecretsMissing = useClusterConnectorSecrets(clusterId);

  const canProceedWithExtraction = () => {
    const isClusterHealthy = clusterStatus === CLUSTER_STATUS.HEALTHY;
    const areSecretsAvailable = !areIdpConnectorSecretsMissing;
    return (
      isClusterHealthy && areSecretsAvailable && !!documentForExtraction && !isUploading && !structuredExtractionResults
    );
  };

  const handleTabChange = (newTab) => {
    const validTab = getValidTab(newTab);
    if (validTab !== selectedTab) {
      setSelectedTab(validTab);
      history.push(`/idp-projects/${slug}/${validTab}`);
    }
  };

  /**
   * @returns {Promise<void>}
   */
  const handleExtractClick = async () => {
    await structuredExtractionStore.extractDocumentTemplate(
      getIdFromSlug(slug),
      idpApplicationStore.idpApplication.clusterId,
      documentForExtraction.id
    );
    if (structuredExtractionStore.structuredExtractionResults) {
      setSelectedTab('extract');
      history.push(`/idp-projects/${slug}/extract`);
    }
  };

  /**
   * @param {File[]} files
   * @returns {Promise<void>}
   */
  const handleDocumentUpload = async (files) => {
    await structuredExtractionStore.uploadDocumentForExtraction(getIdFromSlug(slug), files);
  };

  /**
   * @param {string} documentId
   * @returns {Promise<void>}
   */
  const handleDeleteDocument = async (documentId) => {
    await structuredExtractionStore.deleteDocumentForExtraction(documentId);
  };

  useEffect(() => {
    // If there is not clusterDocumentId, it's not a saved extraction document
    if (documentForExtraction?.clusterDocumentId) {
      structuredExtractionStore.loadStructuredResultsForDocument(documentForExtraction.id);
    }
  }, [documentForExtraction?.clusterDocumentId, documentForExtraction?.id]);

  useEffect(() => {
    if (!testTemplateDocuments?.length) {
      return;
    }
    // If there is not clusterDocumentId, it's not a saved test template document
    loadStructuredResultsForTestTemplateDocuments(
      testTemplateDocuments.filter((doc) => doc.clusterDocumentId).map((doc) => doc.id)
    );
  }, [loadStructuredResultsForTestTemplateDocuments, testTemplateDocuments]);

  useEffect(() => {
    const validTab = getValidTab(tabParam);
    if (validTab !== tabParam) {
      setSelectedTab(DEFAULT_TAB);
      history.replace(`/idp-projects/${slug}/${DEFAULT_TAB}`);
    } else {
      setSelectedTab(validTab);
    }
  }, [tabParam, slug]);

  useEffect(() => {
    const id = getIdFromSlug(slug);
    const controller = new AbortController();
    structuredExtractionStore.init(id, controller.signal);

    return () => {
      controller.abort();
      structuredExtractionStore.reset();
    };
  }, [slug]);

  return (
    <>
      {renderTitleContainer}
      <ExtractionTabContainer
        tabs={VALID_TABS}
        tabIndices={{ uploadDocuments: 0, extractData: 1, validateExtraction: 2 }}
        selectedTabIndex={VALID_TABS.indexOf(selectedTab)}
        setSelectedTabIndex={(i) => handleTabChange(VALID_TABS[i])}
        tabTitles={['Upload documents', 'Extracted results', 'Test template']}
        tabContents={[
          <DocumentUpload
            canUserModifyProject={canUserModifyProject}
            documents={documentForExtraction ? [documentForExtraction] : []}
            onExtractClick={handleExtractClick}
            canProceedWithExtraction={canProceedWithExtraction()}
            isExtracting={isExtractingDocumentTemplate}
            labels={{
              titleText: documentForExtraction ? `Extract ${documentForExtraction.name}` : 'Upload a document',
              descriptionText: documentForExtraction
                ? 'Click below to extract data from the selected document.'
                : 'Drag and drop the document you want to use as a template for structured document extraction.',
              dropzoneLabelText: 'Drag and drop files here or click to upload a document',
              extractButtonText: documentForExtraction ? 'Extract selected document' : 'Extract document',
              extractingInProgressText: 'Extracting document...'
            }}
            onFilesUpload={handleDocumentUpload}
            onDeleteDocument={handleDeleteDocument}
          />,
          <Styled.TabContentWrapper>
            <ExtractedResults
              allDocuments={[documentForExtraction]}
              isPdfViewerVisible={isPdfViewerVisible}
              onTogglePdfViewer={setIsPdfViewerVisible}
            />
          </Styled.TabContentWrapper>,
          <TestTemplate canUserModifyProject={canUserModifyProject} />
        ]}
        renderRightHeader={() =>
          selectedTab === 'evaluate' &&
          canUserModifyProject && <PublishTemplate handleNavigationToVersions={handleNavigationToVersions} />
        }
        disabledTabIndices={[...(!structuredExtractionResults ? [1, 2] : [])]}
        canUserModifyProject={canUserModifyProject}
      />
    </>
  );
});
